import React from 'react';

import Field from '@atlaskit/form/field';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import Select from '@atlaskit/select/select';
import type { OptionType, ValueType } from '@atlaskit/select/types';
import { defineMessage, useIntl } from 'react-intl';

const formatLabelMessage = defineMessage({
	id: 'townsquare.teams-settings.people-format-select.format-label',
	defaultMessage: 'Format',
	description: 'Label for the people field format dropdown',
});

const formatPlaceholderMessage = defineMessage({
	id: 'townsquare.teams-settings.custom-field-form.format-placeholder',
	defaultMessage: 'Select a format',
	description: 'Placeholder for the people format dropdown',
});

const formatIndividualsMessage = defineMessage({
	id: 'townsquare.teams-settings.custom-field-form.format-individuals',
	defaultMessage: 'Individuals',
	description: 'Individuals format option for people field',
});

const formatTeamMessage = defineMessage({
	id: 'townsquare.teams-settings.custom-field-form.format-team',
	defaultMessage: 'Team',
	description:
		'Drop-down item label for the Team format option when configuring a people-type custom field.',
});

import type { StringOption } from '../types';
import { renderCustomFieldIcon } from '../utils';

type PeopleFormatSelectProps = {
	mode: 'create' | 'edit';
	initialValue?: ValueType<StringOption> | null;
};

export const PeopleFormatSelect = ({
	mode,
	initialValue,
}: PeopleFormatSelectProps): React.JSX.Element => {
	const intl = useIntl();
	const formatOptions = [
		{
			label: intl.formatMessage(formatIndividualsMessage),
			value: 'USER',
			icon: renderCustomFieldIcon('USER'),
		},
		{
			label: intl.formatMessage(formatTeamMessage),
			value: 'TEAM',
			icon: renderCustomFieldIcon('TEAM'),
		},
	];

	const resolvedDefaultValue = initialValue
		? (() => {
				const match = formatOptions.find(
					(option) => option.value === (initialValue as StringOption)?.value,
				);
				return match ? { label: match.label, value: match.value } : null;
			})()
		: null;

	return (
		/* eslint-disable-next-line @atlaskit/design-system/use-simple-field -- TODO: migrate to the simple field implementation */
		<Field<ValueType<OptionType>>
			name="peopleFormat"
			label={intl.formatMessage(formatLabelMessage)}
			defaultValue={resolvedDefaultValue}
			isRequired
		>
			{({ fieldProps }) => (
				/* eslint-disable-next-line @atlaskit/design-system/no-placeholder -- TODO: migrate away from placeholder usage */
				<Select
					{...fieldProps}
					options={formatOptions}
					isDisabled={mode === 'edit'}
					placeholder={intl.formatMessage(formatPlaceholderMessage)}
					formatOptionLabel={(option, { context }) => (
						<Inline space="space.100" alignBlock="center">
							{option.icon}
							<Text color={context === 'value' ? 'color.text.subtlest' : undefined}>
								{option.label}
							</Text>
						</Inline>
					)}
				/>
			)}
		</Field>
	);
};
